<script setup>
// The manager page's signature instrument: HeroFlow's grammar, three times over.
//
// HeroFlow taught the reader a vocabulary — the pand on the left, the organisation's
// OWN account on the right, a 2px ink rail between them, and Pandig floating OFF that
// rail on a dashed stub that never reaches it, captioned "via Pandig · € 0,00". This
// card speaks it in the plural: three rails, three DIFFERENT masked IBANs, three
// different amounts, under one header that says a single account. "Gescheiden
// gehouden" and "één account, meerdere VvE's" are then both true structurally, drawn
// rather than claimed — which is the whole reason this card exists instead of a
// sentence.
//
// The figures are illustrative, in the register the homepage already uses
// ("38 eigenaren", "NL·· ···· ··21").
//
// Motion: the rails draw top to bottom, a cobalt pulse rides each one in hard
// mechanical steps (no blur, no shadow on the moving dot), and each destination chip
// flips secondary → primary as its pulse lands — the site's established
// origin → destination language. Reduced motion gets the settled state at once, with
// no pulse dots rendered at all.
//
// What deliberately does NOT animate is the money — HeroFlow's rule, and it matters
// more here. Three amounts ticking up from zero would, for about a second, read as
// three real collection shortfalls on the one instrument whose entire job is to be
// trusted. Every amount is final from the first readable frame.
import { ref, onMounted, onUnmounted } from "vue";
import { Landmark } from "@lucide/vue";
import BrandMark from "@/components/BrandMark.vue";
import { formatMoney } from "@/lib/format";
import { gsap, useMotion, reducedMotion, collapseRail, EASE } from "@/lib/motion";

const props = defineProps({
  topLabel: { type: String, default: "Eén account · drie VvE's" },
  viaLabel: { type: String, default: "via Pandig" },
  viaValue: { type: String, default: "€ 0,00" },
  footLabel: { type: String, default: "Elke VvE haar eigen rekening · geen tussenrekening" },
  // The ghosted row under the three: it states scale without claiming a feature.
  nextLabel: { type: String, default: "+ de volgende VvE, hetzelfde account" },
  startDelay: { type: Number, default: 0.15 },
});

const vves = [
  { name: "Hoefkade 12", cents: 475000, iban: "NL·· ···· ··21" },
  { name: "Parkzicht 4", cents: 231000, iban: "NL·· ···· ··08" },
  { name: "De Vaart 88", cents: 189000, iban: "NL·· ···· ··47" },
];

// Which destination chips the pulse has reached. Reduced motion starts arrived, so
// the card's resting state is the same picture either way.
const arrived = ref(vves.map(() => reducedMotion));

const root = ref(null);
const viaChip = ref(null);
const railLines = ref([]);
const railArrows = ref([]);
const pulseDots = ref([]);

let cleanup = () => {};

onMounted(() => {
  cleanup = useMotion(root.value, () => {
    if (reducedMotion) return;

    const railTips = railLines.value.map((line) => collapseRail(line));
    gsap.set(root.value, { opacity: 0, x: -5, y: -14 });
    gsap.set(viaChip.value, { opacity: 0, y: -6 });
    gsap.set(railArrows.value, { opacity: 0 });

    // A "draw" label marks the moment the card has landed; every rail hangs off it at
    // its own offset rather than off GSAP's "<"/">" shortcuts, which only ever refer
    // to the single most-recently-added tween and would mis-sequence three parallel
    // rails silently.
    const tl = gsap
      .timeline({ delay: props.startDelay })
      .to(root.value, { opacity: 1, x: 0, y: 0, duration: 0.42, ease: EASE })
      .to(viaChip.value, { opacity: 1, y: 0, duration: 0.3, ease: EASE }, "-=0.12")
      .addLabel("draw", "-=0.05");

    vves.forEach((_, i) => {
      const at = `draw+=${i * 0.18}`;
      const lands = `draw+=${i * 0.18 + 1.05}`;

      tl.to(railLines.value[i], { attr: { x2: railTips[i] }, duration: 0.4, ease: "power2.inOut" }, at)
        .to(railArrows.value[i], { opacity: 1, duration: 0.15 }, `${at}+=0.4`)
        .to(pulseDots.value[i], { opacity: 1, duration: 0.05 }, `${at}+=0.4`)
        .to(pulseDots.value[i], { left: "90%", duration: 0.75, ease: "steps(12)" }, `${at}+=0.4`)
        .to(pulseDots.value[i], { opacity: 0, duration: 0.1 }, lands)
        .call(() => (arrived.value[i] = true), null, lands);
    });
  });
});

onUnmounted(() => cleanup());
</script>

<template>
  <figure ref="root" class="border-2 border-foreground bg-card p-2.5 text-foreground shadow-[var(--shadow-hard-lg)] sm:p-7">
    <figcaption class="font-mono text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground sm:text-[0.6875rem]">
      {{ topLabel }}
    </figcaption>

    <!-- The chip hangs above the whole stack rather than above one rail: it is one
         Pandig over three VvE's, which is the "één account" half of the claim. The
         top margin is what leaves it its room, and its dashed stub stops short of the
         first rail — the gap IS the argument, so it never depends on a track
         happening to be tall enough to contain it.
         The margin has to clear the chip AND its stub, measured: chip + stub is 37px
         at phone size, so mt-7 (28px) had the chip sitting on the caption. -->
    <div class="relative mt-10 sm:mt-12">
      <div ref="viaChip" class="absolute inset-x-0 bottom-full flex flex-col items-center">
        <span class="whitespace-nowrap border-2 border-foreground bg-card px-2 py-0.5 font-mono text-[0.625rem] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          {{ viaLabel }} · {{ viaValue }}
        </span>
        <span class="mt-1 h-2.5 border-l-2 border-dashed border-foreground/35 sm:mt-1.5 sm:h-4" aria-hidden="true" />
      </div>

      <ul class="grid gap-1.5 sm:gap-4">
        <li v-for="(vve, i) in vves" :key="vve.name">
          <div class="grid grid-cols-[auto_1fr_auto] items-center gap-2 sm:gap-4">
            <span class="flex size-6 items-center justify-center border-2 border-foreground bg-secondary text-foreground sm:size-9">
              <BrandMark class="size-3.5 text-primary sm:size-4" />
            </span>

            <div class="relative h-6 sm:h-9">
              <div class="absolute inset-x-0 top-1/2 -translate-y-1/2">
                <svg class="h-4 w-full" viewBox="0 0 100 20" preserveAspectRatio="none" aria-hidden="true">
                  <line
                    :ref="(el) => (railLines[i] = el)"
                    x1="1"
                    y1="10"
                    x2="92"
                    y2="10"
                    stroke="currentColor"
                    stroke-width="2"
                    vector-effect="non-scaling-stroke"
                  />
                  <path
                    :ref="(el) => (railArrows[i] = el)"
                    d="M88 4 L97 10 L88 16"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    vector-effect="non-scaling-stroke"
                  />
                </svg>
                <span
                  v-if="!reducedMotion"
                  :ref="(el) => (pulseDots[i] = el)"
                  class="absolute left-[2%] top-1/2 size-2 -translate-y-1/2 border border-foreground bg-primary opacity-0"
                  aria-hidden="true"
                />
              </div>
            </div>

            <span
              class="flex size-6 items-center justify-center border-2 border-foreground transition-colors duration-200 sm:size-9"
              :class="arrived[i] ? 'bg-primary text-primary-foreground' : 'bg-secondary text-primary'"
            >
              <Landmark class="size-3.5 sm:size-4" />
            </span>
          </div>

          <!-- The readout under each rail. Name and figure share a line where there is
               room and the figure drops beneath it where there isn't — three rows have
               to fit the screen HeroFlow spends on one. The IBAN keeps its own line
               either way: broken mid-number it stops reading as an account at all. -->
          <div class="flex flex-wrap items-baseline justify-between gap-x-3 font-mono text-[0.625rem] font-semibold uppercase leading-tight tracking-[0.08em] sm:mt-1.5 sm:text-[0.6875rem]">
            <span class="text-muted-foreground">{{ vve.name }}</span>
            <span class="text-primary">
              <span class="tabular-nums">{{ formatMoney(vve.cents) }}</span>
              <span class="text-muted-foreground"> · <span class="whitespace-nowrap">{{ vve.iban }}</span></span>
            </span>
          </div>
        </li>
      </ul>
    </div>

    <!-- The ghosted next row states scale without claiming a feature. It sits out the
         phone layout, the way the hero's middle fact does: three rails already say
         "portefeuille" on a screen where the argument above them needs the height. -->
    <p class="mt-2.5 hidden border-2 border-dashed border-foreground/25 px-2 py-1 text-center font-mono text-[0.625rem] uppercase tracking-[0.08em] text-muted-foreground sm:mt-4 sm:block sm:py-1.5 sm:text-[0.6875rem]">
      {{ nextLabel }}
    </p>

    <p class="mt-2 border-t-2 border-foreground/10 pt-1.5 font-mono text-[0.625rem] uppercase tracking-[0.12em] text-muted-foreground sm:mt-5 sm:pt-4 sm:text-[0.6875rem]">
      {{ footLabel }}
    </p>
  </figure>
</template>
